import { useEffect, useRef, useState } from 'react';
import { Monitor, Moon, Sun } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useTheme, type Theme } from '../lib/useTheme';

const OPTIONS: { value: Theme; label: string; icon: typeof Sun }[] = [
  { value: 'system', label: 'System', icon: Monitor },
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
];

export function ThemeToggle() {
  const { theme, setTheme, effectiveTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);
  const Icon = effectiveTheme === 'dark' ? Moon : Sun;
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)} className="rounded-full p-2 transition-colors hover:bg-neutral-200 dark:hover:bg-neutral-700" aria-label="Toggle theme">
        <Icon className="h-5 w-5 text-type-800 dark:text-neutral-200" />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -10 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: -10 }} transition={{ duration: 0.15 }}
            className="absolute right-0 z-50 mt-2 w-36 rounded-xl border border-neutral-200 bg-white py-2 shadow-lg dark:border-neutral-700 dark:bg-neutral-800"
          >
            {OPTIONS.map((o) => (
              <button
                key={o.value} onClick={() => { setTheme(o.value); setOpen(false); }}
                className={`flex w-full items-center gap-3 px-4 py-2 text-left transition-colors ${theme === o.value ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300' : 'text-type-800 hover:bg-neutral-100 dark:text-neutral-200 dark:hover:bg-neutral-700'}`}
              >
                <o.icon className="h-4 w-4" />
                <span className="text-sm font-medium">{o.label}</span>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
